import React from "react";
import styles from "./CodeEditorMenu.module.scss";
import ModalConfirmContent from "../Modals/ModalConfirmContent";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/sharp-regular-svg-icons/faEdit";
import {useCsContext} from "../../state/CsInstance";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {faCat} from "@fortawesome/sharp-regular-svg-icons/faCat";
import {fa0} from "@fortawesome/sharp-regular-svg-icons/fa0";
import {faChevronRight} from "@fortawesome/sharp-regular-svg-icons/faChevronRight";
import {faMinus} from "@fortawesome/sharp-regular-svg-icons/faMinus";
import {faPlus} from "@fortawesome/sharp-regular-svg-icons/faPlus";
import {faWindows} from "@fortawesome/free-brands-svg-icons/faWindows";
import {faCommand} from "@fortawesome/sharp-regular-svg-icons/faCommand";
import {DEFAULT_EDITOR_FONT_SIZE} from "../../state/uiStore";

export type CodeEditorMenuProps = {
	setCodeEditorMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
	codeEditorMenuOpen: boolean;
};
const RESET_TITLE = "Are you sure you want to reset this project?";
const RESET_TEXT =
	"This will undo all changes you made to all the files in this project.";
const CLEAR_TITLE = "Are you sure you want to reset the IDE?";
const CLEAR_TEXT =
	"This will undo all changes you made to ALL the projects. This should only be done as a last resort.";

const CodeEditorMenu = ({
	setCodeEditorMenuOpen,
	codeEditorMenuOpen,
}: CodeEditorMenuProps) => {
	const {codeStore, uiStore, globalModalStore} = useCsContext();
	const [codeEditorSubmenuOpen, setCodeEditorSubmenuOpen] =
		React.useState(false);
	const [resetDisabled, setResetDisabled] = React.useState(true);

	function onKeyDownHandler(event: KeyboardEvent) {
		if (event.key === "Shift") {
			setResetDisabled(false);
		}
	}

	function onKeyUpHandler(event: KeyboardEvent) {
		if (event.key === "Shift") {
			setResetDisabled(true);
		}
	}

	// Menu measurements (menusurements) for positioning the tab submenu
	const menuWidth = document.getElementById("codeEditorMenu")?.offsetWidth;
	const menuHeight = document.getElementById("codeEditorMenu")?.offsetHeight;

	/**
	 * Creates a menu item for the code editor menu.
	 * @param text The text to display in the menu item.
	 * @param hotkey The hotkey associated with the menu item keyboard command.
	 */
	function codeEditorMenuItem(text: string, hotkey?: IconProp) {
		// Determines if the user is on an iOS device so we can show the correct metakey icon.
		function isIos() {
			return (
				navigator.userAgent.includes("Mac") ||
				navigator.userAgent.includes("iPhone") ||
				navigator.userAgent.includes("iPad")
			);
		}

		const metaKey = isIos() ? faCommand : faWindows;

		/**
		 * Pre-text area for the menu item. If the text is "Open Tabs", it will display the number of open tabs. Otherwise, it will be empty.
		 */
		function preTextArea() {
			if (text === "Open Tabs") {
				return (
					<div className={styles.fileCount}>
						{codeStore.filesOpen.filter((f) => !!f).length}
					</div>
				);
			}
			return <div className={[styles.fileCount, styles.empty].join(" ")}></div>;
		}

		/**
		 * Post-text area for the menu item. If the text is "Open Tabs", it will display the chevron icon. Otherwise, it will display the keyboard commands for the menu item action if applicable and will be empty if not.
		 */
		function postTextArea() {
			if (!hotkey) {
				hotkey = faCat; // Dummy value, we still need to render the hotkey div for formatting purposes
			}
			if (text === "Open Tabs") {
				return (
					<div className={styles.openTabsIcon}>
						<FontAwesomeIcon icon={faChevronRight} className={styles.icon} />
					</div>
				);
			}

			return (
				<div
					className={styles.hotkey}
					style={hotkey === faCat ? {visibility: "hidden"} : {}}
				>
					<FontAwesomeIcon icon={metaKey as IconProp} className={styles.icon} />
					<FontAwesomeIcon icon={faPlus} className={styles.plusIcon} />
					<FontAwesomeIcon icon={hotkey} className={styles.icon} />{" "}
				</div>
			);
		}

		function onMenuItemClick() {
			switch (text) {
				case "Zoom in":
					uiStore.increaseFontSize();
					setCodeEditorMenuOpen(false);
					break;

				case "Zoom out":
					uiStore.decreaseFontSize();
					setCodeEditorMenuOpen(false);
					break;

				case "Zoom to 50%":
					uiStore.directlySetFontSize(DEFAULT_EDITOR_FONT_SIZE * 0.5);
					setCodeEditorMenuOpen(false);

					break;

				case "Zoom to 100%":
					uiStore.resetFontSize();
					setCodeEditorMenuOpen(false);
					break;

				case "Zoom to 200%":
					uiStore.directlySetFontSize(DEFAULT_EDITOR_FONT_SIZE * 2);
					setCodeEditorMenuOpen(false);
					break;

				case "Open Tabs":
					setCodeEditorSubmenuOpen(!codeEditorSubmenuOpen);
					break;

				case "Reset this project":
					globalModalStore.showModal(
						<ModalConfirmContent
							title={RESET_TITLE}
							text={RESET_TEXT}
							isErrorVariant={true}
							onSuccess={() => codeStore.resetProject()}
						/>
					);
					setCodeEditorMenuOpen(false);
					break;

				case "Reset IDE":
					globalModalStore.showModal(
						<ModalConfirmContent
							title={CLEAR_TITLE}
							text={CLEAR_TEXT}
							isErrorVariant={true}
							onSuccess={() => codeStore.resetLocalStorageAndReload()}
						/>
					);
					setCodeEditorMenuOpen(false);
					break;
			}
		}

		function disabled() {
			if (text === "Reset this project" || text === "Reset IDE") {
				return resetDisabled;
			}
			return false;
		}

		return (
			<button
				className={
					disabled()
						? [styles.option, styles.disabled].join(" ")
						: styles.option
				}
				onClick={() => (disabled() ? null : onMenuItemClick())}
			>
				{preTextArea()}
				<div className={styles.text}>{text}</div>
				{postTextArea()}
			</button>
		);
	}

	/**
	 * Creates a submenu item for the code editor menu.
	 * @param text The text to display in the submenu item, which is the name of the file to open.
	 */
	function codeEditorSubmenuItem(text: string) {
		function onClickSubmenuItem() {
			if (codeStore.focusedFile === text) {
				return;
			}
			const fileData = codeStore.visibleFileMap[text];
			if (!fileData) {
				return;
			}
			codeStore.focusFile(fileData);
			setCodeEditorMenuOpen(false);
			setCodeEditorSubmenuOpen(false);
		}

		const classNames = [styles.option];
		if (codeStore.focusedFile === text) {
			classNames.push(styles.disabled);
		}
		return (
			<button
				className={classNames.join(" ")}
				key={text}
				onClick={onClickSubmenuItem}
			>
				<FontAwesomeIcon icon={faEdit} />
				<div className={styles.text}>{text}</div>
			</button>
		);
	}

	React.useEffect(() => {
		window.addEventListener("keydown", onKeyDownHandler);
		window.addEventListener("keyup", onKeyUpHandler);
		return () => {
			window.removeEventListener("keydown", onKeyDownHandler);
			window.removeEventListener("keyup", onKeyUpHandler);
		};
	}, []);

	return (
		<>
			<div
				className={styles.menu}
				id="codeEditorMenu"
				style={{
					visibility: codeEditorMenuOpen ? "visible" : "hidden",
				}}
			>
				<div className={styles.optionContainer}>
					<div className={styles.containerHeader}>Text Resizing</div>
					{codeEditorMenuItem("Zoom in", faPlus)}
					{codeEditorMenuItem("Zoom out", faMinus)}
					{codeEditorMenuItem("Zoom to 50%")}
					{codeEditorMenuItem("Zoom to 100%", fa0)}
					{codeEditorMenuItem("Zoom to 200%")}
				</div>

				<div className={styles.optionContainer}>
					<div className={styles.containerHeader}>Resetting</div>
					{/* {codeEditorMenuItem("Reset this project")} */}
					{codeEditorMenuItem("Reset IDE")}
				</div>

				<div className={styles.divider}></div>

				<div className={styles.optionContainer}>
					{codeEditorMenuItem("Open Tabs")}
				</div>
			</div>
			<div
				className={[styles.menu, styles.tabSubMenu].join(" ")}
				style={{
					right: menuWidth,
					top: menuHeight,
					visibility:
						codeEditorSubmenuOpen && codeEditorMenuOpen ? "visible" : "hidden",
				}}
			>
				<div
					className={[styles.submenuContent, styles.optionContainer].join(" ")}
				>
					<div className={styles.containerHeader}>Open Tabs</div>
					{codeStore.filesOpen
						.filter((f) => !!f)
						.map((fileName) => {
							return codeEditorSubmenuItem(fileName);
						})}
				</div>
			</div>
		</>
	);
};

export default CodeEditorMenu;
